import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

/* ========== LUMEN admin (operator-facing, unchanged functionality) ========== */
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLogin from "@/pages/Login";

import Dashboard from "@/pages/admin/Dashboard";
import Customers from "@/pages/admin/Customers";
import Lines from "@/pages/admin/Lines";
import Channels from "@/pages/admin/Channels";
import Categories from "@/pages/admin/Categories";
import Nodes from "@/pages/admin/Nodes";
import Analytics from "@/pages/admin/Analytics";
import Settings from "@/pages/admin/Settings";
import Sources from "@/pages/admin/Sources";
import Transcoding from "@/pages/admin/Transcoding";
import Vod from "@/pages/admin/Vod";
import Bouquets from "@/pages/admin/Bouquets";
import Epg from "@/pages/admin/Epg";
import Audit from "@/pages/admin/Audit";
import AdminNotifications from "@/pages/admin/Notifications";
import AdminMarketing from "@/pages/admin/Marketing";
import Tools from "@/pages/admin/Tools";
import StreamsLive from "@/pages/admin/StreamsLive";

/* ========== SaaS (customer-facing) ========== */
import { SaasAuthProvider } from "@/saas/context/SaasAuthContext";
import SaasProtectedRoute from "@/saas/components/SaasProtectedRoute";

import Landing     from "@/saas/pages/Landing";
import SaasLogin   from "@/saas/pages/Login";
import Register    from "@/saas/pages/Register";
import Forgot      from "@/saas/pages/Forgot";
import SaasDashboard from "@/saas/pages/Dashboard";
import Plans       from "@/saas/pages/Plans";
import Devices     from "@/saas/pages/Devices";
import AppDownload from "@/saas/pages/AppDownload";
import Player      from "@/saas/pages/Player";

function AdminLoginOrRedirect() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg">
        <div className="w-14 h-14 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return user ? <Navigate to="/admin" replace /> : <AdminLogin />;
}

const ADMIN_ROUTES = [
  ["/admin",             Dashboard],
  ["/admin/customers",   Customers],
  ["/admin/lines",       Lines],
  ["/admin/channels",    Channels],
  ["/admin/categories",  Categories],
  ["/admin/sources",     Sources],
  ["/admin/transcoding", Transcoding],
  ["/admin/vod",         Vod],
  ["/admin/bouquets",    Bouquets],
  ["/admin/epg",         Epg],
  ["/admin/nodes",       Nodes],
  ["/admin/analytics",   Analytics],
  ["/admin/audit",         Audit],
  ["/admin/notifications", AdminNotifications],
  ["/admin/marketing",     AdminMarketing],
  ["/admin/tools",         Tools],
  ["/admin/streams-live",  StreamsLive],
  ["/admin/settings",      Settings],
];

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <SaasAuthProvider>
          <Toaster
            position="top-right"
            theme="dark"
            richColors
            closeButton
            toastOptions={{ duration: 3500 }}
          />
          <BrowserRouter>
            <Routes>
              {/* ======== SaaS public ======== */}
              <Route path="/"         element={<Landing />} />
              <Route path="/login"    element={<SaasLogin />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot"   element={<Forgot />} />
              <Route path="/plans"    element={<Plans />} />
              <Route path="/app"      element={<AppDownload />} />

              {/* ======== SaaS authenticated ======== */}
              <Route path="/dashboard" element={<SaasProtectedRoute><SaasDashboard /></SaasProtectedRoute>} />
              <Route path="/devices"   element={<SaasProtectedRoute><Devices /></SaasProtectedRoute>} />
              <Route path="/watch"     element={<SaasProtectedRoute><Player /></SaasProtectedRoute>} />

              {/* ======== LUMEN admin ======== */}
              <Route path="/admin/login" element={<AdminLoginOrRedirect />} />
              {ADMIN_ROUTES.map(([path, Comp]) => (
                <Route
                  key={path}
                  path={path}
                  element={<ProtectedRoute><Comp /></ProtectedRoute>}
                />
              ))}

              {/* ======== Fallback ======== */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </SaasAuthProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
